import React, { useEffect, useMemo, useState } from 'react';
import FormControl from '@mui/joy/FormControl';
import FormLabel from '@mui/joy/FormLabel';
import FormHelperText from '@mui/joy/FormHelperText';
import Select from '@mui/joy/Select';
import Option from '@mui/joy/Option';
import Grid from '@mui/joy/Grid';
import CircularProgress from '@mui/joy/CircularProgress';
import {
  fetchLocalidadesByProvincia,
  fetchProvinciasArgentina,
  GeorefLocalidad,
  GeorefProvincia,
  matchLocalidadNombre,
  matchProvinciaNombre,
} from '../../services/GeorefArgentinaApi';

export interface ArgentinaProvinceCityFieldsProps {
  readonly stateValue: string;
  readonly cityValue: string;
  readonly onStateCityChange: (state: string, city: string) => void;
}

const ArgentinaProvinceCityFields: React.FC<ArgentinaProvinceCityFieldsProps> = ({
  stateValue,
  cityValue,
  onStateCityChange,
}) => {
  const [provincias, setProvincias] = useState<GeorefProvincia[]>([]);
  const [localidades, setLocalidades] = useState<GeorefLocalidad[]>([]);
  const [loadingProvincias, setLoadingProvincias] = useState(true);
  const [loadingLocalidades, setLoadingLocalidades] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async (): Promise<void> => {
      setLoadingProvincias(true);
      setErrorMessage(null);
      try {
        const list = await fetchProvinciasArgentina();
        if (!cancelled) {
          setProvincias(list);
        }
      } catch {
        if (!cancelled) {
          setErrorMessage('No se pudieron cargar las provincias. Reintentá más tarde.');
        }
      } finally {
        if (!cancelled) {
          setLoadingProvincias(false);
        }
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  const selectedProvinciaId = useMemo((): string | null => {
    if (!stateValue || provincias.length === 0) {
      return null;
    }
    return matchProvinciaNombre(provincias, stateValue)?.id ?? null;
  }, [stateValue, provincias]);

  useEffect(() => {
    if (!selectedProvinciaId) {
      setLocalidades([]);
      setLoadingLocalidades(false);
      return;
    }
    let cancelled = false;
    setLocalidades([]);
    setLoadingLocalidades(true);
    setErrorMessage(null);
    const load = async (): Promise<void> => {
      try {
        const list = await fetchLocalidadesByProvincia(selectedProvinciaId);
        if (!cancelled) {
          setLocalidades(list);
        }
      } catch {
        if (!cancelled) {
          setLocalidades([]);
          setErrorMessage('No se pudieron cargar las ciudades de esta provincia.');
        }
      } finally {
        if (!cancelled) {
          setLoadingLocalidades(false);
        }
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [selectedProvinciaId]);

  const selectedLocalidadId = useMemo((): string | null => {
    if (!cityValue || localidades.length === 0) {
      return null;
    }
    return matchLocalidadNombre(localidades, cityValue)?.id ?? null;
  }, [cityValue, localidades]);

  const handleProvinciaChange = (
    _event: React.SyntheticEvent | null,
    value: string | null,
  ): void => {
    if (!value) {
      onStateCityChange('', '');
      return;
    }
    const provincia = provincias.find((p) => p.id === value);
    if (!provincia) {
      return;
    }
    onStateCityChange(provincia.nombre, '');
  };

  const handleLocalidadChange = (
    _event: React.SyntheticEvent | null,
    value: string | null,
  ): void => {
    if (!value || !selectedProvinciaId) {
      const prov = provincias.find((p) => p.id === selectedProvinciaId);
      onStateCityChange(prov?.nombre ?? stateValue, '');
      return;
    }
    const loc = localidades.find((l) => l.id === value);
    const prov = provincias.find((p) => p.id === selectedProvinciaId);
    if (!loc || !prov) {
      return;
    }
    onStateCityChange(prov.nombre, loc.nombre);
  };

  return (
    <>
      <Grid xs={12} md={6}>
        <FormControl required>
          <FormLabel>Provincia</FormLabel>
          <Select
            placeholder={loadingProvincias ? 'Cargando…' : 'Elegí una provincia'}
            value={selectedProvinciaId}
            onChange={handleProvinciaChange}
            disabled={loadingProvincias}
            endDecorator={loadingProvincias ? <CircularProgress size="sm" /> : null}
            slotProps={{
              listbox: {
                sx: { maxHeight: 320, overflow: 'auto' },
              },
            }}
          >
            {provincias.map((p) => (
              <Option key={p.id} value={p.id}>
                {p.nombre}
              </Option>
            ))}
          </Select>
          {errorMessage && provincias.length === 0 ? (
            <FormHelperText color="danger">{errorMessage}</FormHelperText>
          ) : null}
        </FormControl>
      </Grid>
      <Grid xs={12} md={6}>
        <FormControl required>
          <FormLabel>Ciudad / localidad</FormLabel>
          <Select
            placeholder={
              !selectedProvinciaId
                ? 'Primero elegí una provincia'
                : loadingLocalidades
                  ? 'Cargando…'
                  : 'Elegí una ciudad'
            }
            value={selectedLocalidadId}
            onChange={handleLocalidadChange}
            disabled={!selectedProvinciaId || loadingLocalidades}
            endDecorator={loadingLocalidades ? <CircularProgress size="sm" /> : null}
            slotProps={{
              listbox: {
                sx: { maxHeight: 320, overflow: 'auto' },
              },
            }}
          >
            {localidades.map((l) => (
              <Option key={l.id} value={l.id}>
                {l.nombre}
              </Option>
            ))}
          </Select>
          {errorMessage && provincias.length > 0 && localidades.length === 0 && !loadingLocalidades ? (
            <FormHelperText color="danger">{errorMessage}</FormHelperText>
          ) : null}
        </FormControl>
      </Grid>
    </>
  );
};

export default ArgentinaProvinceCityFields;
